import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Product", href: "#product", type: "hash" as const },
    { label: "Pricing", href: "/pricing", type: "route" as const },
  ];

  const linkHref = (link: typeof links[number] | string) => {
    if (typeof link === "string") {
      return isHome ? link : `/${link}`;
    }
    return link.type === "route" ? link.href : isHome ? link.href : `/${link.href}`;
  };

  const focusWaitlistEmail = () => {
    if (!isHome) return;

    window.requestAnimationFrame(() => {
      document.querySelector<HTMLInputElement>("#waitlist-email")?.focus({ preventScroll: true });
    });
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/60 backdrop-blur-2xl border-b border-border/20" : "bg-transparent border-b border-transparent"}`}>
      <div className="container-wide flex items-center justify-between h-16">
        <a href="/" className="flex items-center gap-2">
          <span
            className="text-xl font-bold tracking-tight shimmer-text"
            style={{ fontFamily: "'Clash Display', 'Lexend', system-ui, sans-serif" }}
          >
            Ant<span>Atlas</span>
          </span>
        </a>

        {/* Desktop */}
        <div className="hidden items-center gap-8">
          {links.map((l) => (
            <a
              key={l.label}
              href={linkHref(l)}
              className="text-[13px] font-medium text-muted-foreground/80 hover:text-foreground transition-colors duration-200 uppercase tracking-[0.08em]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Button variant="accent" size="sm" asChild>
            <a href={linkHref("#waitlist")} onClick={focusWaitlistEmail}>
              Book a demo
            </a>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-muted-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background/90 backdrop-blur-2xl border-b border-border/20 px-6 pb-6 animate-fade-in">
          <div className="flex flex-col gap-4 pt-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={linkHref(l)}
                className="hidden text-sm font-medium text-muted-foreground/80 hover:text-foreground uppercase tracking-[0.08em]"
                onClick={() => setMobileOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-3 pt-4">
              <Button variant="accent" className="flex-1" asChild>
                <a
                  href={linkHref("#waitlist")}
                  onClick={() => {
                    setMobileOpen(false);
                    focusWaitlistEmail();
                  }}
                >
                  Book a demo
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
